import { Container } from 'react-bootstrap';
import ContactLink from '../Utilities/ContactLink';
import { HttpStatusCode } from '../../types';
import useHtmlTitle from '../Utilities/useHtmlTitle';
import useBodyClass from '../Utilities/useBodyClass';
import HeaderIntroText from '../HeaderIntroText';

const ServerError = () => {
    useHtmlTitle('Server error | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - An unexpected error has occurred
            </h1>
            <p className='mb-6'>
                Please wait a minute before reloading this page to try again.
                <br />
                Alternatively please go back to the dashboard to start over.
            </p>
            <ContactLink />
        </Container>
    );
};

const ForbiddenError = () => {
    useHtmlTitle('Forbidden | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - Forbidden
                <span className='visually-hidden'> 404</span>
            </h1>
            <HeaderIntroText className='mb-2'>
                The page you&apos;re looking for is not accessible.
            </HeaderIntroText>
            <p className='mb-6'>
                Please check the web address you entered is correct and try again or return to the dashboard to continue.
            </p>
            <ContactLink />
        </Container>
    );
};

const ConflictError = () => {
    useHtmlTitle('Conflict | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - Conflict
                <span className='visually-hidden'> 404</span>
            </h1>
            <HeaderIntroText className='mb-2'>
                The record you are editing cannot be updated because the existing record is newer.
            </HeaderIntroText>
            <p className='mb-6'>
                Please check the web address you entered is correct and try again or return to the dashboard to continue.
            </p>
            <ContactLink />
        </Container>
    );
};

const NoLongerAvailableError = () => {
    useHtmlTitle('Not Available | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - Not Available
                <span className='visually-hidden'> 404</span>
            </h1>
            <HeaderIntroText className='mb-2'>
                The page you&apos;re looking for is no longer availble.
            </HeaderIntroText>
            <p className='mb-6'>
                Please check the web address you entered is correct and try again or return to the dashboard to continue.
            </p>
            <ContactLink />
        </Container>
    );
};

const NotFound = () => {
    useHtmlTitle('Page not found | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - Page not found
                <span className='visually-hidden'> 404</span>
            </h1>
            <HeaderIntroText className='mb-2'>
                The page you&apos;re looking for no longer exists or has moved.
            </HeaderIntroText>
            <p className='mb-6'>
                Please check the web address you entered is correct and try again or return to the dashboard to continue.
            </p>
            <ContactLink />
        </Container>
    );
};

const ServiceUnavailableError = () => {
    useHtmlTitle('Service Unavailable | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - Service unavailable.
                <span className='visually-hidden'> Unknown error</span>
            </h1>
            <HeaderIntroText className='mb-2'>
                The page you&apos;re trying to access has encountered an error.
            </HeaderIntroText>
            <p className='mb-6'>Please wait a few minutes before trying again or return to the dashboard to continue.</p>
            <ContactLink />
        </Container>
    );
};

const PreconditionFailedError = () => {
    useHtmlTitle('Precondition Failed | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - A precondition failed error has occurred
                <span className='visually-hidden'> Unknown error</span>
            </h1>
            <HeaderIntroText className='mb-2'>
                The page you&apos;re trying to access has encountered an error.
            </HeaderIntroText>
            <p className='mb-6'>Please wait a few minutes before trying again or return to the dashboard to continue.</p>
            <ContactLink />
        </Container>
    );
};

const UnprocessableError = () => {
    useHtmlTitle('Unprocessable | NMI Services portal');
    useBodyClass('http-error');
    return (
        <Container className='py-5'>
            <h1 id='page-title' tabIndex={-1}>
                Oops - Unprocessable.
                <span className='visually-hidden'> Unprocessable entity </span>
            </h1>
            <HeaderIntroText className='mb-2'>
                The page you&apos;re trying to access has encountered an error.
            </HeaderIntroText>
            <p className='mb-6'>Please wait a few minutes before trying again or return to the dashboard to continue.</p>
            <ContactLink />
        </Container>
    );
};

const ErrorDisplay = (props: { status: number }) => {
    const { status } = props;
    switch (status) {
        case HttpStatusCode.Conflict:
            return (<ConflictError />);
        case HttpStatusCode.Gone:
            return (<NoLongerAvailableError />);
        case HttpStatusCode.NotFound:
            return <NotFound />;
        case HttpStatusCode.PreconditionFailed:
            return (<PreconditionFailedError />);
        case HttpStatusCode.ServiceUnavailable:
            return (<ServiceUnavailableError />);
        case HttpStatusCode.UnprocessableEntity:
            return (<UnprocessableError />);
        case HttpStatusCode.Forbidden:
            return (<ForbiddenError />);
        case HttpStatusCode.InternalServerError:
            return (<ServerError />);
        default:
            return (<ServerError />);
    }
};

export default ErrorDisplay;
